import React, { createContext, useContext, useMemo, useEffect, useState } from "react";
import { useUser, useOrganization, useAuth } from "@clerk/clerk-react";
import { useQuery } from "convex/react";
import { api } from "../../../../convex/_generated/api";
import {
  ClerkRole,
  AppRole,
  AppPermission,
  UserRoleContext,
  isClerkRole,
  ROUTE_PERMISSIONS,
} from "@/types/auth";
import {
  getUserPermissions,
  hasPermission,
  hasAnyPermission,
  createUserRoleContext,
  canAccessRoute,
  createPermissionChecker,
  debugPermissions,
} from "@/lib/permissions";

interface AuthContextValue {
  // Authentication state
  isAuthenticated: boolean;
  isLoading: boolean;

  // User identity
  userId?: string;
  organizationId?: string;
  sessionToken?: string;

  // Clerk data
  clerkRole: ClerkRole;

  // Application data
  appRole: AppRole;
  permissions: AppPermission[];
  isOwner: boolean;
  roleFlags?: Record<string, boolean>;

  // Helper functions
  hasPermission: (permission: AppPermission) => boolean;
  hasAnyPermission: (permissions: AppPermission[]) => boolean;
  canAccessRoute: (route: string) => boolean;
  getSecureHeaders: () => Record<string, string>;
  refreshAuth: () => Promise<void>;

  // User role context
  userRoleContext: UserRoleContext | null;

  // Personal workspace support
  hasPersonalWorkspace: boolean;
  hasOrganization: boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthContext must be used within AuthProvider");
  }
  return context;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { user, isLoaded: userLoaded } = useUser();
  const { organization, isLoaded: orgLoaded } = useOrganization();
  const { getToken, isSignedIn } = useAuth();
  const [sessionToken, setSessionToken] = useState<string>();

  // Only make Convex calls when user is properly authenticated
  const shouldMakeConvexCalls = isSignedIn && userLoaded && user?.id;

  // Get user data from Convex database
  const userData = useQuery(api.users.getCurrentUser, shouldMakeConvexCalls ? {} : "skip");

  // Extract Clerk role from organization membership
  const clerkRole = useMemo((): ClerkRole => {
    if (!user?.organizationMemberships?.length) return "org:member";

    const membership =
      user.organizationMemberships.find((m) => m.organization.id === organization?.id) ||
      user.organizationMemberships[0];

    const role = membership?.role;
    return isClerkRole(role) ? role : "org:member";
  }, [user, organization]);

  // TEMPORARY FIX: Handle case where userData might be undefined
  // Determine if user is owner and get role flags
  const { isOwner, roleFlags, appRole } = useMemo(() => {
    if (!userData) {
      // Fallback to basic role determination from Clerk role
      const fallbackIsOwner = false;
      const fallbackRoleFlags = undefined;
      const fallbackAppRole: AppRole = clerkRole === "org:admin" ? "admin" : "member";
      return { isOwner: fallbackIsOwner, roleFlags: fallbackRoleFlags, appRole: fallbackAppRole };
    }

    const isOwner = userData.isOwner || false;
    const roleFlags = userData.roleFlags as Record<string, boolean> | undefined;

    // Determine app role based on database role or derive from Clerk role
    let appRole: AppRole = "member";
    if (userData.role) {
      // Use database role if available (for backward compatibility)
      switch (userData.role) {
        case "owner":
          appRole = "owner";
          break;
        case "admin":
          appRole = "admin";
          break;
        case "manager":
          appRole = "manager";
          break;
        case "member":
        case "viewer":
        default:
          appRole = "member";
          break;
      }
    } else {
      // Derive from Clerk role and flags
      if (isOwner) {
        appRole = "owner";
      } else if (roleFlags?.manager && clerkRole === "org:member") {
        appRole = "manager";
      } else if (clerkRole === "org:admin") {
        appRole = "admin";
      } else {
        appRole = "member";
      }
    }
    // For personal workspace users (no org), grant owner-level access
    if (!userData.orgId && userData.hasPersonalWorkspace) {
      return { isOwner: true, roleFlags: undefined, appRole: "owner" as AppRole };
    }

    return { isOwner, roleFlags, appRole };
  }, [userData, clerkRole]);

  // Get user permissions based on hybrid role system
  const permissions = useMemo(() => {
    return getUserPermissions(clerkRole, appRole, isOwner, roleFlags);
  }, [clerkRole, appRole, isOwner, roleFlags]);

  // TEMPORARY FIX: Create user role context even without userData
  // Create user role context
  const userRoleContext = useMemo((): UserRoleContext | null => {
    if (!user?.id) return null;

    const context = createUserRoleContext(user.id, organization?.id, clerkRole, isOwner, roleFlags);

    // Debug permissions in development
    if (process.env.NODE_ENV === "development") {
      debugPermissions(context, "AuthContext");
    }

    return context;
  }, [user?.id, organization?.id, clerkRole, isOwner, roleFlags]); // Removed userData dependency

  // Get session token from Clerk
  useEffect(() => {
    const getSessionToken = async () => {
      if (user?.id) {
        try {
          // Get the actual JWT session token from Clerk for Convex authentication
          // Use "convex" as the audience to match auth.config.ts
          const token = await getToken({
            template: "convex",
          });
          if (token) {
            setSessionToken(`Bearer ${token}`);
            console.log("🔐 Got Clerk JWT token for Convex authentication");
          } else {
            console.warn("⚠️ No JWT token available from Clerk");
            setSessionToken(undefined);
          }
        } catch (error) {
          console.error("Failed to get session token:", error);
          setSessionToken(undefined);
        }
      } else {
        setSessionToken(undefined);
      }
    };

    if (userLoaded) {
      getSessionToken();
    }
  }, [user, userLoaded, getToken]);

  // Permission checking functions
  const checkPermission = createPermissionChecker(permissions);

  const checkAnyPermission = (requiredPermissions: AppPermission[]): boolean => {
    return hasAnyPermission(permissions, requiredPermissions);
  };

  const checkRouteAccess = (route: string): boolean => {
    return canAccessRoute(permissions, route, ROUTE_PERMISSIONS);
  };

  // Get secure headers for API requests
  const getSecureHeaders = (): Record<string, string> => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (user?.id) {
      headers["X-User-ID"] = user.id;
    }

    if (organization?.id) {
      headers["X-Organization-ID"] = organization.id;
    }

    if (appRole) {
      headers["X-App-Role"] = appRole;
    }

    if (clerkRole) {
      headers["X-Clerk-Role"] = clerkRole;
    }

    if (sessionToken) {
      headers["Authorization"] = sessionToken;
    }

    return headers;
  };

  // Refresh authentication state
  const refreshAuth = async (): Promise<void> => {
    if (user?.id) {
      try {
        // Refresh session token with actual JWT from Clerk
        const token = await getToken({
          template: "convex",
        });
        if (token) {
          setSessionToken(`Bearer ${token}`);
          console.log("🔄 Refreshed Clerk JWT token");
        } else {
          console.warn("⚠️ No JWT token available during refresh");
          setSessionToken(undefined);
        }
      } catch (error) {
        console.error("Failed to refresh auth:", error);
      }
    }
  };

  // Wait for user data to be loaded to prevent authentication issues
  // Note: Don't wait for orgLoaded for personal workspace users who may not have an org
  const isLoading = !userLoaded || (user?.id && userData === undefined);

  // Users are authenticated if they have either an organization OR a personal workspace
  const hasOrganization = !!userData?.orgId;
  const hasPersonalWorkspace = !!userData?.hasPersonalWorkspace;
  const isAuthenticated =
    !!user && !!sessionToken && !!userData && (hasOrganization || hasPersonalWorkspace);

  const contextValue: AuthContextValue = {
    // Authentication state
    isAuthenticated,
    isLoading: !!isLoading,

    // User identity
    userId: user?.id,
    organizationId: organization?.id,
    sessionToken,

    // Clerk data
    clerkRole,

    // Application data
    appRole,
    permissions,
    isOwner,
    roleFlags,

    // Helper functions
    hasPermission: checkPermission,
    hasAnyPermission: checkAnyPermission,
    canAccessRoute: checkRouteAccess,
    getSecureHeaders,
    refreshAuth,

    // User role context
    userRoleContext,

    // Personal workspace support
    hasPersonalWorkspace,
    hasOrganization,
  };

  // TEMPORARY FIX: Only show loading for Clerk, not Convex data
  // Show loading spinner while data is loading
  if (isLoading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50">
        <img src="/mc-icon.svg" alt="Loading" className="h-16 w-16 animate-spin" />
        <p className="mt-4 text-gray-500">Loading...</p>
      </div>
    );
  }

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

// Hook for getting authentication status
export function useAuthStatus() {
  const {
    isAuthenticated,
    isLoading,
    clerkRole,
    appRole,
    permissions,
    isOwner,
    hasPermission,
    canAccessRoute,
  } = useAuthContext();

  return {
    isAuthenticated,
    isLoading,
    clerkRole,
    appRole,
    permissions,
    isOwner,
    hasPermission,
    canAccessRoute,
    // Convenience permission checks
    canManageTeam: hasPermission("team:write"),
    canViewAnalytics: hasPermission("analytics:read"),
    canManageClients: hasPermission("clients:write"),
    canManageSettings: hasPermission("settings:write"),
  };
}

// Hook for secure API calls
export function useSecureApiCall() {
  const { getSecureHeaders, isAuthenticated } = useAuthContext();

  const makeSecureRequest = async (url: string, options: RequestInit = {}): Promise<Response> => {
    if (!isAuthenticated) {
      throw new Error("User not authenticated");
    }

    const headers = {
      ...getSecureHeaders(),
      ...options.headers,
    };

    return fetch(url, {
      ...options,
      headers,
    });
  };

  return { makeSecureRequest, isAuthenticated };
}

// Hook for permission-based operations
export function usePermissions() {
  const {
    hasPermission,
    hasAnyPermission,
    canAccessRoute,
    permissions,
    appRole,
    isOwner,
    userRoleContext,
  } = useAuthContext();

  const requirePermission = (permission: AppPermission, context?: string): void => {
    if (!hasPermission(permission)) {
      throw new Error(
        `Missing required permission: ${permission}${context ? ` for ${context}` : ""}`
      );
    }
  };

  const checkPermissionWithFeedback = (
    permission: AppPermission
  ): {
    allowed: boolean;
    reason?: string;
  } => {
    if (hasPermission(permission)) {
      return { allowed: true };
    }

    return {
      allowed: false,
      reason: `Your role (${appRole}) does not have permission: ${permission}`,
    };
  };

  return {
    hasPermission,
    hasAnyPermission,
    canAccessRoute,
    requirePermission,
    checkPermissionWithFeedback,
    permissions,
    appRole,
    isOwner,
    userRoleContext,
  };
}

// Convenience hook for common auth operations
export function useAppAuth() {
  return useAuthContext();
}
